// src/pages/ServiceProviderOnboarding.tsx: first-time provider setup wizard
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import LocationSelect from '../components/LocationSelect';
import {
  C, CATEGORIES, REGIONS, PRICE_MODELS, CURRENCIES, uploadAsset,
} from '../lib/serviceConnect';

const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const inputStyle: React.CSSProperties = { width: '100%', background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px', outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', display: 'block', marginBottom: '5px' };

const STEPS = ['Basics', 'About', 'First Product', 'Media'];

export default function ServiceProviderOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [step, setStep]         = useState(0);
  const [submitting, setSub]    = useState(false);
  const [error, setError]       = useState('');

  const [basics, setBasics] = useState({ name: '', category: CATEGORIES[0], headquarters_city: '', headquarters_country: '', website: '' });
  const [about, setAbout]   = useState({ description: '', regions_covered: [] as string[], founded_year: '', client_count: '', linkedin: '' });
  const [product, setProduct] = useState({ name: '', description: '', price_from: '', price_to: '', price_model: PRICE_MODELS[0], price_currency: 'USD', is_flagship: true });

  const [logoFile, setLogoFile]   = useState<File | null>(null);
  const [deckFile, setDeckFile]   = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from('service_providers').select('id').eq('owner_id', user.id).maybeSingle();
      if (data) { navigate('/service-provider-dashboard', { replace: true }); return; }
      setChecking(false);
    })();
  }, [user, navigate]);

  const toggleRegion = (r: string) => setAbout(a => ({ ...a, regions_covered: a.regions_covered.includes(r) ? a.regions_covered.filter(x => x !== r) : [...a.regions_covered, r] }));

  const canNext = () => {
    if (step === 0) return basics.name.trim().length > 0;
    return true;
  };

  const finish = async () => {
    if (!user) return;
    setSub(true); setError('');
    try {
      // Upload assets first (folder = owner uid)
      let logo_url: string | null = null, pitch_deck_url: string | null = null, demo_video_url: string | null = null;
      if (logoFile)  logo_url = await uploadAsset(user.id, 'logo', logoFile);
      if (deckFile)  pitch_deck_url = await uploadAsset(user.id, 'deck', deckFile);
      if (videoFile) demo_video_url = await uploadAsset(user.id, 'video', videoFile);

      const { data: provider, error: pErr } = await supabase.from('service_providers').insert({
        owner_id: user.id,
        name: basics.name.trim(), category: basics.category,
        headquarters_city: basics.headquarters_city.trim() || null,
        headquarters_country: basics.headquarters_country.trim() || null,
        website: basics.website.trim() || null,
        description: about.description.trim() || null,
        regions_covered: about.regions_covered,
        founded_year: about.founded_year ? parseInt(about.founded_year) : null,
        client_count: about.client_count ? parseInt(about.client_count) : null,
        linkedin: about.linkedin.trim() || null,
        logo_url, pitch_deck_url, demo_video_url,
        account_status: 'active',
      }).select().single();
      if (pErr) throw new Error(pErr.message);

      if (product.name.trim()) {
        await supabase.from('service_provider_products').insert({
          provider_id: provider.id, name: product.name.trim(),
          description: product.description.trim() || null,
          price_from: product.price_from.trim() || null, price_to: product.price_to.trim() || null,
          price_model: product.price_model, price_currency: product.price_currency, is_flagship: product.is_flagship,
        });
      }
      navigate('/service-provider-dashboard', { replace: true });
    } catch (e: any) {
      setError(e.message || 'Something went wrong.');
      setSub(false);
    }
  };

  if (checking) return (
    <div style={{ display: 'flex', height: '70vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading…</div>
    </div>
  );

  return (
    <div style={{ padding: '40px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '26px', color: C.navy, fontWeight: 400, margin: '0 0 6px', letterSpacing: '-0.3px' }}>Set up your ServiceConnect profile</h1>
          <p style={{ fontSize: '13.5px', color: C.muted, margin: 0 }}>A few quick steps to get your firm in front of alternative investment managers.</p>
        </div>

        {/* Progress */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            {STEPS.map((s, i) => (
              <div key={s} style={{ fontSize: '11px', fontWeight: i === step ? 700 : 600, color: i <= step ? C.navy : C.mutedLt, letterSpacing: '0.3px' }}>
                {i + 1}. {s}
              </div>
            ))}
          </div>
          <div style={{ height: '4px', background: C.borderLt, borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${((step + 1) / STEPS.length) * 100}%`, background: C.gold, transition: 'width 0.25s' }} />
          </div>
        </div>

        <div style={{ ...card, padding: '28px 30px' }}>
          {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '18px', fontSize: '12.5px', color: C.danger }}>{error}</div>}

          {step === 0 && (
            <>
              <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Company Name *</label><input style={inputStyle} value={basics.name} onChange={e => setBasics(b => ({ ...b, name: e.target.value }))} placeholder="Acme Fund Services" /></div>
              <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Category *</label>
                <select style={inputStyle} value={basics.category} onChange={e => setBasics(b => ({ ...b, category: e.target.value }))}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <LocationSelect
                  country={basics.headquarters_country} city={basics.headquarters_city}
                  onCountryChange={v => setBasics(b => ({ ...b, headquarters_country: v }))}
                  onCityChange={v => setBasics(b => ({ ...b, headquarters_city: v }))}
                  countryLabel="HQ Country" cityLabel="HQ City"
                  labelStyle={labelStyle} inputStyle={inputStyle}
                />
              </div>
              <div><label style={labelStyle}>Website</label><input style={inputStyle} value={basics.website} onChange={e => setBasics(b => ({ ...b, website: e.target.value }))} placeholder="acme.com" /></div>
            </>
          )}

          {step === 1 && (
            <>
              <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Description</label>
                <textarea rows={5} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={about.description} onChange={e => setAbout(a => ({ ...a, description: e.target.value }))} placeholder="What does your firm do, and who do you serve?" />
              </div>
              <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Regions Covered</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {REGIONS.map(r => {
                    const on = about.regions_covered.includes(r);
                    return <button key={r} type="button" onClick={() => toggleRegion(r)} style={{ padding: '6px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500, border: `1px solid ${on ? C.navy : C.border}`, background: on ? C.navy : C.white, color: on ? C.white : C.textSec }}>{r}</button>;
                  })}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div><label style={labelStyle}>Founded Year</label><input type="number" style={inputStyle} value={about.founded_year} onChange={e => setAbout(a => ({ ...a, founded_year: e.target.value }))} placeholder="2015" /></div>
                <div><label style={labelStyle}>Client Count</label><input type="number" style={inputStyle} value={about.client_count} onChange={e => setAbout(a => ({ ...a, client_count: e.target.value }))} placeholder="40" /></div>
              </div>
              <div><label style={labelStyle}>LinkedIn</label><input style={inputStyle} value={about.linkedin} onChange={e => setAbout(a => ({ ...a, linkedin: e.target.value }))} placeholder="linkedin.com/company/…" /></div>
            </>
          )}

          {step === 2 && (
            <>
              <p style={{ fontSize: '12.5px', color: C.muted, margin: '0 0 18px', lineHeight: 1.6 }}>Add your first product or service. You can add more later from your dashboard. (Optional, you can skip this step.)</p>
              <div style={{ marginBottom: '14px' }}><label style={labelStyle}>Product Name</label><input style={inputStyle} value={product.name} onChange={e => setProduct(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Full-Service Fund Administration" /></div>
              <div style={{ marginBottom: '14px' }}><label style={labelStyle}>Description</label><textarea rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={product.description} onChange={e => setProduct(p => ({ ...p, description: e.target.value }))} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div><label style={labelStyle}>Price From</label><input style={inputStyle} value={product.price_from} onChange={e => setProduct(p => ({ ...p, price_from: e.target.value }))} placeholder="10,000" /></div>
                <div><label style={labelStyle}>Price To</label><input style={inputStyle} value={product.price_to} onChange={e => setProduct(p => ({ ...p, price_to: e.target.value }))} placeholder="50,000" /></div>
                <div><label style={labelStyle}>Currency</label><select style={inputStyle} value={product.price_currency} onChange={e => setProduct(p => ({ ...p, price_currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></div>
                <div><label style={labelStyle}>Model</label><select style={inputStyle} value={product.price_model} onChange={e => setProduct(p => ({ ...p, price_model: e.target.value }))}>{PRICE_MODELS.map(m => <option key={m}>{m}</option>)}</select></div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.textSec, cursor: 'pointer' }}>
                <input type="checkbox" checked={product.is_flagship} onChange={e => setProduct(p => ({ ...p, is_flagship: e.target.checked }))} /> Mark as flagship product
              </label>
            </>
          )}

          {step === 3 && (
            <>
              <p style={{ fontSize: '12.5px', color: C.muted, margin: '0 0 18px', lineHeight: 1.6 }}>Upload your logo and, optionally, a pitch deck and demo video. All optional, you can add them later.</p>
              <FilePick label="Logo" accept="image/*" file={logoFile} onPick={setLogoFile} />
              <FilePick label="Pitch Deck (PDF)" accept="application/pdf" file={deckFile} onPick={setDeckFile} />
              <FilePick label="Demo Video (MP4/MOV)" accept="video/mp4,video/quicktime,video/*" file={videoFile} onPick={f => { setVideoFile(f); if (videoPreview) URL.revokeObjectURL(videoPreview); setVideoPreview(f ? URL.createObjectURL(f) : null); }} />
              {videoPreview && (
                <div style={{ marginTop: '10px' }}>
                  <video src={videoPreview} controls style={{ width: '100%', borderRadius: '4px', background: C.black, maxHeight: '280px' }} />
                </div>
              )}
            </>
          )}

          {/* Nav */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '26px', paddingTop: '20px', borderTop: `1px solid ${C.borderLt}` }}>
            <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0 || submitting} style={{ background: C.white, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '10px 20px', fontSize: '12.5px', fontWeight: 600, cursor: step === 0 ? 'not-allowed' : 'pointer', fontFamily: 'system-ui', opacity: step === 0 ? 0.5 : 1 }}>Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={() => canNext() && setStep(s => s + 1)} disabled={!canNext()} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '10px 26px', fontSize: '12.5px', fontWeight: 600, cursor: canNext() ? 'pointer' : 'not-allowed', fontFamily: 'system-ui', opacity: canNext() ? 1 : 0.6 }}>Continue</button>
            ) : (
              <button onClick={finish} disabled={submitting} style={{ background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '10px 26px', fontSize: '12.5px', fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'system-ui', opacity: submitting ? 0.7 : 1 }}>{submitting ? 'Finishing…' : 'Finish & Go to Dashboard'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilePick({ label, accept, file, onPick }: { label: string; accept: string; file: File | null; onPick: (f: File | null) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 0', borderBottom: `1px solid ${C.borderLt}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ ...labelStyle, marginBottom: '3px' }}>{label}</div>
        <div style={{ fontSize: '12px', color: file ? C.text : C.mutedLt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '340px' }}>{file ? file.name : 'No file selected'}</div>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        {file && <button onClick={() => onPick(null)} style={{ background: C.white, color: C.danger, border: `1px solid ${C.danger}44`, borderRadius: '3px', padding: '7px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui' }}>Remove</button>}
        <button onClick={() => ref.current?.click()} style={{ background: C.white, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui' }}>Choose</button>
      </div>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; onPick(f || null); e.target.value = ''; }} />
    </div>
  );
}
